import React, { useState, useRef, useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import noImage from '../../assets/images/no-image.png';
import { addColorLibrary, clearReducer } from '../../Pages/Library/ColorLibary/store/ColorLibrary.action';
import ImgDropzone from '../ImgDropzone/ImgDropzone';
import './CommonColor.css';
import Loader from '../Utility/Loader/Loader';
import ColourSupportingDocs from '../SupportingDocuments/colourSupportingDocs';
import UploadFileAndDocuments from '../SupportingDocuments/UploadFileAndDocuments';
import { createOptions } from '../helper';
import CustomTooltip from '../Tooltip/CustomTooltip';
import { NewInput } from '../PIM/common/NewInput';
import { NewSelectMultiSelect } from '../PIM/common/NewSelectMultiSelect';
import { UploadIcon, DeleteIcon } from '../../bombiix-icon';
import { BombiixModal } from '../BombiixModal';
import { toGroupIds } from './colour-group.helpers';

const valueEmpty = (value) => {
  return value || '';
};

function CommonColorWayAdd({ onOpen, toogle, imageUpload, hideDocument, extraClass = '' }) {
  const { t } = useTranslation('common');
  const reducerData = useSelector((reducerState) => reducerState);
  const { ColorLibraryDetails, UserDetails } = reducerData;
  const { colourSupportingDocs, ColorLibGroup } = ColorLibraryDetails;
  const { userInfo } = UserDetails;
  const dispatch = useDispatch();

  const [imageSrc, setImageSrc] = useState('');
  const [showLoader, setShowLoader] = useState(false);
  const [originalImage, setOriginalImage] = useState('');
  const [showImageUpload, setImgUpload] = useState(false);
  const [removeImage, setRemoveImage] = useState(false);
  const [showUploader, setShowUploader] = useState(false);
  const [docIds, setDocIds] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState([]);

  const childRef = useRef();
  const [groupOption, setGroupOptions] = useState([]);
  useEffect(() => {
    const _groupOption = createOptions(ColorLibGroup, 'name', '_id');
    setGroupOptions(_groupOption);
  }, [ColorLibGroup]);

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm();

  const removeImg = () => {
    setImageSrc('');
    setOriginalImage('');
    setRemoveImage(true);
  };

  useEffect(() => {
    if (!isEmpty(colourSupportingDocs)) {
      const ids = colourSupportingDocs.map((ele) => ele?._id);
      setDocIds([...ids]);
    }
  }, [colourSupportingDocs]);

  const onSubmitMethod = async (data) => {
    if (showLoader) {
      return;
    }
    const obj = {
      ...data,
      newReferences: data.references,
      loggedInUserId: valueEmpty(userInfo?.userId),
      groups: JSON.stringify(selectedGroups),
    };
    if (imageSrc) {
      obj.image = imageSrc;
      obj.fullImage = originalImage;
      obj.removeImage = removeImage;
    }
    if (!isEmpty(docIds)) {
      obj.colourDocs = docIds;
    }

    setShowLoader(true);

    const response = await dispatch(addColorLibrary(obj, t));
    await dispatch(clearReducer());
    setShowLoader(false);
    if (response) {
      toogle();
    }
  };

  const handleImageUpload = async (type) => {
    if (type !== 'close') {
      const croppedFileUrl = await childRef.current.showCroppedImg();
      const { imageRes, originalImg } = croppedFileUrl;
      const ogImage = await originalImg;
      setOriginalImage(ogImage);
      if (imageRes) setImageSrc(imageRes);
    }
    setImgUpload(false);
    setRemoveImage(false);
  };

  const handleGroupChange = (selected) => {
    setSelectedGroups(toGroupIds(selected));
  };

  const imageContainer = () => {
    return (
      <div className="image-container">
        <div className="colorway-image">
          <img className="img-fluid" alt="croppedImage" src={imageSrc || noImage} />
        </div>
        <div className="d-flex justify-content-between flex-column">
          <CustomTooltip placement="right" tooltipText={t('COMMON_COLORWAY_ADD.UPLOAD_IMAGE')}>
            <button
              className="bombiix-icon-container bombiix-icon-container--blue"
              type="button"
              aria-label={t('COMMON_COLORWAY_ADD.UPLOAD_IMAGE')}
              onClick={() => {
                imageUpload();
                setImgUpload(true);
              }}
            >
              <UploadIcon size={20} />
            </button>
          </CustomTooltip>
          {imageSrc && (
            <CustomTooltip placement="right" tooltipText={t('COMMON_COLORWAY_ADD.REMOVE_IMAGE')}>
              <button
                className="bombiix-icon-container bombiix-icon-container--red"
                type="button"
                aria-label={t('COMMON_COLORWAY_ADD.REMOVE_IMAGE')}
                disabled={imageSrc === noImage}
                onClick={() => {
                  removeImg();
                }}
              >
                <DeleteIcon size={20} />
              </button>
            </CustomTooltip>
          )}
        </div>
      </div>
    );
  };

  const namefiled = () => {
    return (
      <div className="common-input-control">
        <NewInput
          label={`${t('COMMON_COLORWAY_ADD.COLOR_ARTWORK_NAME')} *`}
          {...register('name', {
            required: true,
            validate: (value) => !!value.trim() || t('COMMON_COLORWAY_ADD.NO_WHITE_SPACE'),
          })}
          placeholder={t('COMMON_COLORWAY_ADD.COLOR_ARTWORK_NAME')}
          invalid={Boolean(errors.name)}
        />

        {errors.name && <span className="error-message">{t('COMMON_COLORWAY_ADD.NAMES_REQUIRED')}</span>}
      </div>
    );
  };
  const descriptionfiled = () => {
    return (
      <div className="common-input-control">
        <NewInput
          label={t('COMMON_COLORWAY_ADD.COLOR_ARTWORK_DESC')}
          {...register('description')}
          placeholder={t('COMMON_COLORWAY_ADD.COLOR_ARTWORK_DESC')}
        />
      </div>
    );
  };

  const uploadCalled = () => {
    setShowUploader(true);
  };

  const mainFunctionality = () => {
    return (
      <>
        <Row>
          <Col lg="12">
            <div className="d-flex flex-wrap">
              {imageContainer()}
              <div className="flex-fill px-2">
                <Row className="form-row">
                  <Col lg="4">{namefiled()}</Col>
                  <Col lg="4">
                    <div className="common-input-control">
                      <NewInput
                        label={`${t('COMMON_COLORWAY_ADD.COLOR_ARTWORK_REF')} *`}
                        {...register('references', {
                          required: true,
                          validate: (value) => !!value.trim() || t('COMMON_COLORWAY_ADD.NO_WHITE_SPACE'),
                        })}
                        placeholder={t('COMMON_COLORWAY_ADD.COLOR_ARTWORK_REF')}
                        invalid={Boolean(errors.references)}
                      />
                      {errors.references && <span className="error-message">{t('COMMON_COLORWAY_ADD.NAMES_REQUIRED')}</span>}
                    </div>
                  </Col>
                  <Col lg="4">{descriptionfiled()}</Col>

                  <Col lg="4">
                    <div className="common-react-select">
                      <NewSelectMultiSelect
                        label={t('COMMON_COLORWAY_ADD.COLOR_ARTWORK_GROUPS')}
                        isMulti
                        name="group"
                        options={groupOption}
                        getOptionLabel={(option) => `${option.name}`}
                        getOptionValue={(option) => `${option._id}`}
                        value={groupOption.filter((val) => selectedGroups.includes(val._id))}
                        onChange={handleGroupChange}
                        placeholder={t('COMMON_COLORWAY_ADD.COLOR_ARTWORK_GROUPS')}
                        isClearable
                      />
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
          </Col>
        </Row>
        {!hideDocument ? <ColourSupportingDocs libraryId={null} openUpload={uploadCalled} topLoading={setShowLoader} /> : null}
      </>
    );
  };


  const imagesrc = () => {
    return originalImage || ifImgSrc();
  };

  const ifImgSrc = () => {
    return valueEmpty(imageSrc);
  };

  const imguploader = () => {
    return (
      <ImgDropzone ref={childRef} onSubmit={handleImageUpload} image={imagesrc()} acceptedFileExtenstion=".jpeg,.png,.jpg" isEditMode={false} title="colour" />
    );
  };

  const forImageUploading = () => {
    return showImageUpload ? imguploader() : mainFunctionality();
  };

  const toggleUpload = () => {
    setShowUploader(!showUploader);
  };

  const modalBody = () => {
    return showLoader ? <Loader /> : forImageUploading();
  };

  return showUploader ? (
    <UploadFileAndDocuments showTitleField onOpen={showUploader} toggle={toggleUpload} />
  ) : (
    <BombiixModal
      show={onOpen}
      className={`color-way-modal ${extraClass}`}
      size="lg"
      title={t('COMMON_COLORWAY_ADD.ADD_COLOR_ARTWORK')}
      onClose={toogle}
      onSave={handleSubmit(onSubmitMethod)}
      saveLabel={t('COMMON_LABEL.SAVE')}
      hideSave={showImageUpload}
    >
      {modalBody()}
    </BombiixModal>
  );
}

export default CommonColorWayAdd;
